import type { UgcOverview } from './ugcTypes'
export type App = {
  id: string
  slug: string
  name: string
  bundleId: string | null
  platforms: string[]
  domains: string[]
  color: string | null
  createdAt: string
}

export type User = {
  id: string
  email: string
  role: 'owner' | 'limited'
  sectionAcl: Record<string, 'r' | 'rw'> | null
  createdAt: string
}

export type SessionData = {
  userId: string
  createdAt: string
}

export type SecretKey =
  | 'app_store_connect'
  | 'revenuecat'
  | 'github_pat'
  | 'resend'
  | 'posthog'

export type SecretMeta = {
  key: SecretKey
  isSet: boolean
  updatedAt: string | null
}

export type ApiError = {
  error: {
    code: string
    message: string
  }
}

// --- CRM types ---

export type CollaborationType = 'audience' | 'production' | 'both'

export type CompModel = 'flat' | 'gifting' | 'promo_affiliate' | 'hybrid'

export type ContactType = 'ugc_creator' | 'press' | 'partner' | 'sponsor'

export type DealStage =
  | 'prospecte'
  | 'contacte'
  | 'en_discussion'
  | 'accepte'
  | 'brief_envoye'
  | 'contenu_recu'
  | 'publie'
  | 'paye_cloture'
  | 'decline_archive'

export type Social = {
  id: string
  creatorId: string
  platform: string
  handle: string | null
  profileUrl: string | null
  followers: number | null
  engagementRate: number | null
  updatedAt: string
}

export type Creator = {
  id: string
  displayName: string
  collaborationType: CollaborationType
  contactType: ContactType
  organization: string | null
  contactRole: string | null
  niche: string | null
  audienceCountry: string | null
  audienceLanguage: string | null
  defaultRate: number | null
  currency: string
  email: string | null
  notes: string | null
  source: 'manual' | 'application' | 'url'
  socials: Social[]
  createdAt: string
  updatedAt: string
}

// Alias sémantique : le CRM généralisé parle de « contacts »
export type Contact = Creator

export type Campaign = {
  id: string
  appId: string
  name: string
  goal: string | null
  budgetAmount: number | null
  currency: string
  status: 'planning' | 'active' | 'done'
  startDate: string | null
  endDate: string | null
  createdAt: string
}

export type Deliverable = {
  id: string
  dealId: string
  kind: 'own_audience' | 'our_channels'
  contentUrl: string | null
  platform: string | null
  postedAt: string | null
  views: number | null
  installsAttributed: number | null
  revenueAttributed: number | null
  subjectiveRating: number | null
  notes: string | null
  createdAt: string
}

export type Deal = {
  id: string
  creatorId: string
  appId: string
  campaignId: string | null
  stage: DealStage
  compModel: CompModel | null
  amount: number | null
  currency: string
  promoCode: string | null
  affiliateRate: number | null
  dueDate: string | null
  deliverables: Deliverable[]
  createdAt: string
  updatedAt: string
}

// --- CRM compléments (Plan 3) ---

export type MessageChannel =
  | 'email'
  | 'whatsapp'
  | 'instagram_dm'
  | 'tiktok_dm'
  | 'call'
  | 'note'
  | 'other'

export type TemplateLanguage = 'fr' | 'en'

export type MessageTemplate = {
  id: string
  appId: string
  name: string
  channel: MessageChannel
  contactType: ContactType | null
  language: TemplateLanguage
  subject: string | null
  body: string
  sortOrder: number
  createdAt: string
  updatedAt: string
}

// --- Contents (Marketing Ops chantier 2) ---

export type ContentKind = 'ad' | 'organic'

export type ContentFormat = 'video' | 'image' | 'carousel' | 'text' | 'other'

export type ContentPlatform =
  | 'tiktok'
  | 'instagram_reels'
  | 'instagram_post'
  | 'x'
  | 'reddit'
  | 'youtube'
  | 'apple_search_ads'
  | 'app_store'
  | 'other'

export type ContentStatus = 'idea' | 'to_produce' | 'ready' | 'live' | 'archived'

export type Content = {
  id: string
  appId: string
  title: string
  kind: ContentKind
  format: ContentFormat
  platform: ContentPlatform
  language: TemplateLanguage | null
  status: ContentStatus
  hook: string | null
  bodyMd: string
  url: string | null
  plannedDate: string | null
  liveDate: string | null
  budgetAllocated: number | null
  campaignId: string | null
  creatorId: string | null
  views: number | null
  notes: string | null
  createdAt: string
  updatedAt: string
}

// --- Générations vidéo Higgsfield (spec 2026-09-18) ---

export type GenerationStatus = 'queued' | 'in_progress' | 'completed' | 'failed' | 'nsfw' | 'canceled'

export type GenerationResolution = '480p' | '720p' | '1080p' | '4k'

export type GenerationAspectRatio = '16:9' | '4:3' | '1:1' | '3:4' | '9:16' | '21:9'

export type GenerationParams = {
  duration: number
  resolution: GenerationResolution
  aspectRatio: GenerationAspectRatio
  generateAudio: boolean
}

export type Generation = {
  id: string
  contentId: string
  appId: string
  createdBy: string
  model: string
  requestId: string
  status: GenerationStatus
  prompt: string
  params: GenerationParams
  referenceAttachmentIds: string[]
  error: string | null
  outputAttachmentId: string | null
  createdAt: string
  updatedAt: string
  lastPolledAt: string | null
  completedAt: string | null
}

export type MessageDirection = 'in' | 'out'

export type MessageStatus = 'draft' | 'sent' | 'received'

export type Attachment = {
  id: string
  r2Key: string
  filename: string
  contentType: string | null
  size: number | null
  kind: 'brief' | 'contract' | 'message' | 'content'
  linkedType: 'deal' | 'message' | 'creator' | 'content'
  linkedId: string
  createdAt: string
}

export type Message = {
  id: string
  conversationId: string
  channel: MessageChannel
  direction: MessageDirection
  subject: string | null
  body: string | null
  sentAt: string | null
  externalId: string | null
  status: MessageStatus
  createdBy: string | null
  createdAt: string
  attachments: Attachment[]
}

export type Conversation = {
  id: string
  creatorId: string
  lastMessageAt: string | null
  createdAt: string
}

export type TaskStatus = 'todo' | 'doing' | 'done'

export type Task = {
  id: string
  appId: string
  dealId: string | null
  creatorId: string | null
  title: string
  dueDate: string | null
  status: TaskStatus
  createdAt: string
}

export type Application = {
  id: string
  appId: string
  name: string
  email: string | null
  socials: unknown
  message: string | null
  status: 'new' | 'reviewed' | 'accepted' | 'rejected'
  createdAt: string
}

export type UnmatchedEmail = {
  id: string
  fromAddr: string
  subject: string | null
  body: string | null
  receivedAt: string
  createdAt: string
}

// --- Stats types ---

/**
 * Sources collectées : le robot Stats (RevenueCat, App Store Connect) puis celles
 * du rapport quotidien, chantiers 1 et 2 (spec 2026-10-24). Même liste que le CHECK
 * de app_integrations, stats_sync et stats_daily (migration 0014).
 */
export const STATS_SOURCES = [
  'revenuecat', 'app_store_connect', 'meta', 'instagram', 'apple_ads', 'ga4',
  'search_console', 'bing', 'appsflyer', 'posthog', 'sentry', 'tiktok', 'itunes',
] as const

export type StatsSource = (typeof STATS_SOURCES)[number]

export type StatDailySource = StatsSource | 'manual'

export type AppIntegration = {
  id: string
  appId: string
  source: StatsSource
  externalId: string | null
  enabled: boolean
  createdAt: string
}

export type StatDaily = {
  id: string
  appId: string
  date: string
  source: StatDailySource
  metric: string
  value: number
  dimKey: string | null
  dimValue: string | null
  collectedAt: string
}

export type AppReview = {
  id: string
  appId: string
  externalId: string
  rating: number | null
  title: string | null
  body: string | null
  reviewer: string | null
  territory: string | null
  appVersion: string | null
  reviewDate: string | null
  collectedAt: string
}

export type StatsTarget = {
  id: string
  appId: string
  metric: string
  targetValue: number
  dueDate: string | null
  createdAt: string
}

export type StatsSyncStatus = 'ok' | 'error' | 'not_configured'

export type StatsSync = {
  id: string
  appId: string
  source: StatsSource
  status: StatsSyncStatus
  message: string | null
  ranAt: string
}

export type StatsSummary = {
  metrics: Record<string, { current: number; previous: number | null }>
  from: string
  to: string
}

// --- Marketing types ---

export type LaunchPlanStatus =
  | 'planning'
  | 'active'
  | 'launched'
  | 'done'
  | 'archived'

export type LaunchPlan = {
  id: string
  appId: string
  name: string
  launchDate: string | null
  status: LaunchPlanStatus
  createdAt: string
  updatedAt: string
}

export type LaunchTask = {
  id: string
  planId: string
  milestone: string
  milestoneOrder: number
  dayOffset: number | null
  title: string
  detail: string | null
  sortOrder: number
  done: boolean
  doneAt: string | null
  createdAt: string
}

// --- Marketing types ---

export type MarketingDoc = {
  id: string
  appId: string
  slug: string
  title: string
  sortOrder: number
  bodyMd: string
  createdAt: string
  updatedAt: string
}

// --- Marketing — Canaux & Budget ---

export type MarketingChannel =
  | 'reddit'
  | 'product_hunt'
  | 'paid'
  | 'email'
  | 'press'
  | 'other'

export type MarketingAction = {
  id: string
  appId: string
  channel: MarketingChannel
  title: string
  url: string | null
  notes: string | null
  actionDate: string | null
  cost: number | null
  currency: string
  createdAt: string
  updatedAt: string
}

export type BudgetLine = {
  id: string
  appId: string
  category: string
  label: string | null
  planned: number
  actual: number
  currency: string
  createdAt: string
  updatedAt: string
}

// --- Marketing — ASO types ---

export type AsoKeyword = {
  id: string
  appId: string
  locale: string
  term: string
  targetRank: number | null
  notes: string | null
  createdAt: string
  updatedAt: string
}

export type AsoRank = {
  id: string
  keywordId: string
  capturedOn: string
  rank: number | null
  createdAt: string
}

// --- Budget quotidien (Marketing Ops chantier 3) ---

export type ExpenseSource = 'manual' | 'deal_auto' | 'api'

export const EXPENSE_CATEGORY_SUGGESTIONS = [
  'ads', 'ugc', 'outils', 'presse', 'contenu', 'autre',
] as const

export type DailyExpense = {
  id: string
  appId: string
  expenseDate: string
  amount: number
  currency: string
  category: string
  platform: string | null
  contentId: string | null
  dealId: string | null
  source: ExpenseSource
  note: string | null
  createdAt: string
  updatedAt: string
}

// --- Cockpit (Marketing Ops chantier 4) ---

export type CockpitRelance = {
  dealId: string
  creatorId: string
  creatorName: string
  stage: DealStage
  updatedAt: string
}

export type CockpitJalon = {
  id: string
  planId: string
  title: string
  milestone: string
  date: string
}

export type CockpitGateCriterion = {
  metric: string
  target: number
  current: number | null
  met: boolean
  manual: boolean
}

export type CockpitData = {
  tasks: Task[]
  relances: CockpitRelance[]
  contents: { id: string; title: string; status: ContentStatus; plannedDate: string | null }[]
  jalons: CockpitJalon[]
  budget: { today: number; last7: number; monthToDate: number; plannedTotal: number }
  gate: { configured: boolean; allMet: boolean; criteria: CockpitGateCriterion[] }
  /** Compteurs du programme UGC de l'app (spec § 6.7) ; `null` s'ils n'ont pas pu être lus. */
  ugc: UgcOverview | null
}

// --- Finance (spec 2026-08-20) ---

export type {
  FinanceSettings,
  FinanceTaxSettings,
  FinanceDirection,
  IrBracket,
} from '../finance/engine/types'

export type RecurringExpenseKind = 'company' | 'personal'

/** Périodicité d'une récurrente : le montant stocké est ANNUEL pour 'yearly' (lissé /12 en simulation). */
export type ExpensePeriodicity = 'monthly' | 'yearly'

export type RecurringExpense = {
  id: string
  kind: RecurringExpenseKind
  label: string
  amount: number
  currency: string
  active: boolean
  sortOrder: number
  periodicity: ExpensePeriodicity
  /** Mois d'échéance 1-12 d'une dépense annuelle (null = non datée, jamais préremplie dans le Suivi). */
  dueMonth: number | null
  createdAt: string
  updatedAt: string
}

export type FinanceMonthExpense = {
  id: string
  monthId: string
  label: string
  amount: number
  recurringId: string | null
  createdAt: string
  updatedAt: string
}

export type FinanceMonth = {
  id: string
  /** 'YYYY-MM' — unique. */
  month: string
  revenue: number
  salaryPaid: number
  dividendsPaid: number
  note: string | null
  expenses: FinanceMonthExpense[]
  /**
   * Objectifs FIGÉS à la création du mois (migration 0012). `null` = mois créé
   * avant le gel : le Suivi retombe alors sur les objectifs de la simulation
   * courante, en le signalant. Immuables après création (le PUT les ignore).
   */
  targetRevenue: number | null
  targetNet: number | null
  targetExpenses: number | null
  createdAt: string
  updatedAt: string
}

// --- Rapport quotidien (spec 2026-10-24, chantier 1) ---

export type AdChannel = 'meta' | 'apple_ads' | 'google_ads' | 'tiktok'

export type AdLevel = 'campaign' | 'adset' | 'ad' | 'keyword'

export type ReportEventKind =
  | 'app_version'
  | 'ad_change'
  | 'campaign_start'
  | 'campaign_stop'
  | 'budget_change'
  | 'article'
  | 'post'
  | 'measurement'
  | 'manual'

export type AlertSeverity = 'action' | 'watch' | 'good'

export type ReportHorizon = 'day' | '7d' | '30d' | 'all'

/** Performance quotidienne d'une campagne, d'un ensemble, d'une pub ou d'un mot-clé (table ad_daily). */
export type AdDaily = {
  id: string
  appId: string
  date: string
  channel: AdChannel
  level: AdLevel
  entityId: string
  entityName: string
  parentId: string | null
  status: string | null
  currency: string
  spend: number
  spendEur: number
  impressions: number
  clicks: number
  /** Installations vues par la régie ; null si elle n'en donne pas. */
  installs: number | null
  extra: Record<string, number>
  collectedAt: string
}

/** Métrique quotidienne d'attribution par source et campagne (table attribution_daily). */
export type AttributionDaily = {
  id: string
  appId: string
  date: string
  provider: 'appsflyer'
  mediaSource: string
  campaign: string
  metric: string
  value: number
  collectedAt: string
}

/** Événement du journal (versions, changements de pubs, articles, saisies manuelles). */
export type ReportEvent = {
  id: string
  appId: string
  occurredOn: string
  kind: ReportEventKind
  source: string
  title: string
  detail: string | null
  externalRef: string | null
  createdAt: string
}

export type ReportAlert = {
  id: string
  appId: string
  reportDate: string
  ruleKey: string
  severity: AlertSeverity
  title: string
  detail: string | null
  value: number | null
  threshold: number | null
  notifiedAt: string | null
  notifiedVia: 'push' | 'email' | null
  createdAt: string
}

/** Collecte du matin d'un jour de rapport : nombre de sources en succès et en erreur. */
export type ReportDay = {
  id: string
  appId: string
  reportDate: string
  collectedAt: string
  sourcesOk: number
  sourcesError: number
}

export type NarrativeDecision = { title: string; effect: string; evidence: string }

/** Analyse écrite par la routine Claude (schéma fixe validé par reportNarrativeSchema). */
export type ReportNarrativeContent = {
  headline: string[]
  alerts: { severity: AlertSeverity; title: string; detail: string }[]
  decisions: NarrativeDecision[]
  sectionNotes: { budget?: string | undefined; produit?: string | undefined; journal?: string | undefined }
  /** Le lundi seulement : arbitrages de la semaine. */
  arbitrages?: NarrativeDecision[] | undefined
}

export type ReportNarrative = {
  id: string
  appId: string
  reportDate: string
  content: ReportNarrativeContent
  createdAt: string
  updatedAt: string
}

// --- Rapport quotidien : contenu servi (chantier 1, encadré « Re-validation S4 » du plan) ---
// Montants en euros au centième (sauf le revenu, en USD), installations estimées au centième,
// taux entre 0 et 1 non arrondis, dates YYYY-MM-DD. null = « n.d. », jamais un zéro inventé.

/** Période de jours, bornes incluses. */
export type ReportRange = { from: string; to: string }

/** Canal d'installation du rapport : une régie, ou l'organique estimé. */
export type ReportChannel = AdChannel | 'organic'

/** Pub, groupe d'annonces ou campagne, cumulés sur la période. */
export type AdRow = {
  level: AdLevel
  entityId: string
  /** Dernier nom connu sur la période. */
  name: string
  /** Dernier statut connu sur la période. */
  status: string | null
  spendEur: number
  impressions: number
  clicks: number
  /** Installations vues par la régie : Meta (SKAN modélisé, daté à réception), Apple ; Google : null. */
  platformInstalls: number | null
  costPerInstallEur: number | null
}

/** Campagne (level 'campaign') et ses pubs (Meta) ou groupes d'annonces (Apple Ads), par dépense décroissante. */
export type CampaignRow = AdRow & { ads: AdRow[] }

/**
 * Ligne du budget pub par canal (règle de comptage de Léo, 24/10) : Meta, TikTok et Google Ads
 * mesurés par AppsFlyer et estimés par la part d'acceptation du suivi ; Apple Ads au chiffre
 * d'Apple, sans estimation ; organic = organique estimé, sans dépense ni mesure.
 */
export type ChannelRow = {
  channel: ReportChannel
  /** null pour organic, pour TikTok (dépense pas encore relevée) ou une régie non traitée. */
  spendEur: number | null
  /** AppsFlyer (meta, tiktok, google_ads), Apple (apple_ads) ; null pour organic. */
  installsMeasured: number | null
  /** Part des personnes installées qui acceptent le suivi, pour une mesure AppsFlyer ; null pour apple_ads et organic. */
  installsCoverage: number | null
  /** meta, tiktok, google_ads : mesuré ÷ trackingAcceptedShare ; organic : estimation ; apple_ads : null. */
  installsEstimated: number | null
  /** Valeur retenue : apple_ads → installsMeasured ; autres → installsEstimated. */
  installs: number | null
  /** spendEur ÷ installs. */
  costPerInstallEur: number | null
  /** AppsFlyer rc_trial_started_users (meta, tiktok, google_ads) ; null sinon. */
  trialsMeasured: number | null
  /** trialsMeasured ÷ part. */
  trialsEstimated: number | null
  /** spendEur ÷ trialsEstimated. */
  costPerTrialEur: number | null
  campaigns: CampaignRow[]
}

export type FunnelStepKey = 'installs' | 'onboarding_completed' | 'paywall_viewers' | 'trials_started' | 'tracking_allowed'

/** Étape de l'entonnoir par jour d'installation : chaque personne une seule fois, étapes emboîtées. */
export type FunnelStep = {
  key: FunnelStepKey
  /** Personnes installées sur la période, arrivées à l'étape (et aux précédentes). */
  value: number | null
  previous: number | null
  /** ÷ étape précédente ; tracking_allowed : ÷ installs ; null pour installs. */
  rate: number | null
  previousRate: number | null
}

export type ReliabilityState = 'ok' | 'partial' | 'late' | 'error' | 'not_configured' | 'missing'

/**
 * Pastille de fiabilité d'une source intégrée : ok (relevée il y a moins de 36 h), partial (relevé
 * ok avec message, ou jours sans ligne sur la période), late (dernier relevé réussi il y a plus de
 * 36 h), error (dernier relevé en échec), not_configured, missing (aucune donnée sur la période).
 */
export type ReliabilityItem = {
  source: StatsSource
  state: ReliabilityState
  /** ISO du dernier relevé réussi. */
  lastOkAt: string | null
  /** Raison d'un état autre que ok : message du relevé (déjà masqué) ou texte calculé. */
  detail: string | null
  /** Limite connue de la source, fixe. */
  note: string | null
}

/**
 * revenue_net_28d : revenu net RevenueCat des 28 derniers jours au dernier relevé de la période
 * (instantané de l'API Overview), en USD, jamais additionné sur une période. Le revenu d'une
 * période viendra au chantier 2.
 */
export type ReportKpiKey = 'installs' | 'trials' | 'trial_conversions' | 'revenue_net_28d' | 'ad_spend' | 'cost_per_trial'

/** Indicateur du rapport : les six, toujours dans l'ordre de ReportKpiKey. */
export type ReportKpi = {
  key: ReportKpiKey
  value: number | null
  /** Période précédente : null pour « depuis le lancement », ou si elle commence avant dataFrom. */
  previous: number | null
  unit: 'count' | 'eur' | 'usd'
  /** Un point par jour de la période qui a une valeur (revenue_net_28d : instantané du jour). */
  series: { date: string; value: number }[]
  note: string | null
}

/** Jour de la grande courbe : installations retenues par canal, total PostHog et dépense. */
export type MainChartDay = {
  date: string
  /** Valeur retenue du canal ce jour-là, avec la part d'acceptation de la période ; null si n.d. */
  installs: Record<ReportChannel, number | null>
  installsTotal: number | null
  /** Régies relevées ; null si l'une n'est pas traitée ce jour-là. */
  spendEur: number | null
}

/**
 * Premiers téléchargements selon App Store Connect (chiffre officiel d'Apple, rapport de ventes du
 * robot Stats, heure du Pacifique, un à deux jours de retard), à côté du total PostHog des
 * installations. Le filtre des types de produit reste à calibrer (HANDOFF § 8).
 */
export type AppStoreDownloads = {
  /** Somme des jours relevés de la période ; null sans aucun jour relevé. */
  value: number | null
  /** Même somme sur la période précédente ; null pour « depuis le lancement » ou sans jour relevé. */
  previous: number | null
  /** Dernier jour relevé de la période (les jours suivants ne sont pas comptés). */
  through: string | null
}

// --- Rapport quotidien : entrées et sorties de l'API (session S5, plan, encadré « Re-validation S5 ») ---
// Erreurs au format ApiError : 400 validation_error ou bad_request, 401, 404 not_found, 409 conflict.

/** GET /api/reports?appId= : jours de rapport (report_days), du plus récent au plus ancien. */
export type ReportDatesResponse = { dates: string[] }

/** Corps de POST /api/reports/events : événement manuel du journal (kind et source « manual »). */
export type ReportEventCreateInput = {
  appId: string
  /** Jour du calendrier, YYYY-MM-DD. */
  occurredOn: string
  /** 1 à 120 caractères après retrait des espaces autour. */
  title: string
  /** 500 caractères au plus. */
  detail?: string | undefined
}

/** POST /api/reports/events : 201 avec l'événement créé. */
export type ReportEventResponse = { event: ReportEvent }

/** Corps de POST /api/reports/:date/notified (routine, après sa notification push). */
export type ReportNotifiedInput = { via: 'push' }

/** POST /api/reports/:date/notified : alertes action du jour marquées « push ». */
export type ReportNotifiedResponse = { marked: number }

/** PUT /api/reports/:date/narrative et DELETE /api/reports/events/:id?appId= (200). */
export type ReportOkResponse = { ok: true }

/** Contenu du rapport d'un jour (GET /api/reports/:date), calculé à la demande par buildReportPayload. */
export type ReportPayload = {
  appId: string
  reportDate: string
  horizon: ReportHorizon
  range: ReportRange
  previousRange: ReportRange | null
  /** Premier jour du total de référence (PostHog). */
  dataFrom: string | null
  /** La période commence avant dataFrom : chiffres sur les seuls jours relevés. */
  partial: boolean
  /** Relevé du matin du jour du rapport (report_days). */
  collectedAt: string | null
  /**
   * Part des personnes installées sur la période (première installation) qui acceptent le suivi :
   * cohortes PostHog, cohort_att_allowed ÷ cohort_installs (décision de Léo, 25/10). Base des
   * estimations par canal ; AppsFlyer ne démarre qu'après une réponse « accepté ». null si n.d.
   */
  trackingAcceptedShare: number | null
  /** Chiffre officiel d'Apple, à côté du total PostHog. */
  appStoreDownloads: AppStoreDownloads
  reliability: ReliabilityItem[]
  narrative: ReportNarrativeContent | null
  /** Alertes des règles du jour : action, puis à surveiller, puis bonne nouvelle. */
  alerts: ReportAlert[]
  kpis: ReportKpi[]
  /** Jours de max(from, dataFrom) à to ; événements de la période, du plus ancien au plus récent. */
  mainChart: { days: MainChartDay[]; events: ReportEvent[] }
  /** meta, apple_ads, google_ads, tiktok (si présentes), organic. */
  channels: ChannelRow[]
  /** installs, onboarding_completed, paywall_viewers, trials_started. */
  funnel: FunnelStep[]
  /** key 'tracking_allowed'. */
  trackingAllowed: FunnelStep
  /** Événements de la période, du plus récent au plus ancien. */
  journal: ReportEvent[]
  /** Sources d'AppsFlyer sans canal connu qui ont des installations sur la période. */
  unmappedMediaSources: { mediaSource: string; installs: number }[]
}
